import { useEffect, useRef, useState } from 'react'
import { fetchBuild, isStale } from '../api/build'

/**
 * How often to ask, while the app is open and in front of somebody.
 *
 * <b>Five minutes</b> (D-902). This was half an hour, on the reasoning that the
 * check that matters is the one on resume — true of the installed app this was
 * written for, and false of the case that actually turned up: <b>a tab somebody
 * is using continuously never backgrounds</b>, so `visibilitychange` never
 * fires and the interval is the only thing left. The owner tested straight
 * through a deploy on his phone, met the stale build, and never saw this
 * banner.
 *
 * <b>The cost is near zero</b>, which is what makes the old reasoning a bad
 * trade rather than a close one: `/api/version` is anonymous, touches no
 * database and returns a string read once at startup (`BuildVersion`). A
 * hundred and sixty families on a five-minute tick is well under one request a
 * second, and a backgrounded tab has its timers throttled by the browser
 * anyway.
 */
const EVERY = 5 * 60 * 1000

/**
 * Offers a reload when the app running here is not the one being served
 * (D-520).
 *
 * <b>This exists because of the Home Screen install</b> (D-518). A browser tab
 * recovers on the next refresh; a standalone app is resumed for days and has no
 * address bar, no reload button, and no way for anybody to tell its owner to
 * clear a cache.
 *
 * ⚠ <b>Not because of chunks that 404</b> (D-902). This said a deploy replaces
 * every fingerprinted chunk, so an old `index.html` asks for JavaScript that is
 * no longer there — a white screen on a phone. <b>The build has no code
 * splitting</b>: `observed`, the served page references one `index-*.js` and
 * one `index-*.css` and nothing is imported dynamically, so a running app never
 * asks for a second chunk and that cannot happen. The real harm is quieter and
 * has happened: <b>old JavaScript against a new API</b> — fixed bugs
 * reappearing, and a contract that may have moved underneath it.
 *
 * <b>Checked on resume *and* on a timer, and the timer is not a backstop</b>
 * (D-902). `visibilitychange` is the moment an installed app comes back after
 * days away, which is the case this was written for — and it never fires for a
 * tab somebody is using continuously, which is the case that turned up.
 *
 * <b>It offers, it never acts.</b> Reloading somebody out of a half-filled
 * booking form to save them from a fault they have not hit yet is a worse
 * trade than the fault. The prompt stays until it is taken.
 */
export default function NewVersionBanner() {
  const firstSeen = useRef<string | null>(null)
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let cancelled = false
    const controller = new AbortController()

    async function check() {
      const latest = await fetchBuild(controller.signal)
      if (cancelled || latest === null) return

      // The first answer is the baseline, not a comparison.
      if (firstSeen.current === null) {
        firstSeen.current = latest
        return
      }

      if (isStale(firstSeen.current, latest)) setStale(true)
    }

    void check()

    const timer = setInterval(check, EVERY)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }

    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      controller.abort()
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  if (!stale) return null

  return (
    /*
     * <b>Sticky, or the one person who needs it cannot see it</b> (D-902).
     *
     * ⚠ The shell comment above this in `App.tsx` said it sat above the nav
     * "so it cannot be scrolled past", and that was never true: it is an
     * ordinary block at the top of a column, and the phone nav is
     * `fixed bottom-0`. So anyone reading a conversation had scrolled it out of
     * the window, which is exactly where a stale app is most likely to be
     * noticed misbehaving. Reported by the owner as the banner not existing at
     * all - he had never seen one.
     *
     * It costs nothing on an ordinary day, because this component draws `null`
     * unless the served build has actually moved on. `z-40` matches the phone
     * nav rather than beating it: the two never meet, one being pinned to each
     * edge, and the nav menu stays above both at `z-50`.
     */
    <div
      role="status"
      className="sticky top-0 z-40 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 bg-accent px-4 py-2 text-center text-[12.5px] font-semibold text-on-accent"
    >
      <span>A newer version of the studio app is ready.</span>

      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-lg bg-on-accent/15 px-3 py-1 font-semibold underline underline-offset-2 hover:bg-on-accent/25"
      >
        Reload now
      </button>
    </div>
  )
}
